import { createFileRoute } from "@tanstack/react-router"
import {
  getPagedWorkingTimeBookings,
  WorkingTimeBookingDto,
  WorkingTimeImportStatus,
} from "@/orval/gen"
import { PersonName } from "@components/PersonName"
import { Page } from "@components/Page"
import { getFormattedDate } from "@/utils/get-formatted-date"
import {
  InfiniteScrollingTable,
  RowProps,
} from "@/routes/-components/custom-tanstack-table"
import {
  ResponsiveFilterGroup,
  useResponsiveFilterQueryOptions,
} from "../../-components/hideable-filter-group"
import { DatePickerInput, DatesRangeValue } from "@mantine/dates"
import { Stack } from "@mantine/core"
import { InfinitePersonSingleSelect } from "@components/SelectFromQuery/InfinitePersonSingleSelect"
import { ColumnDef, Row, SortingState } from "@tanstack/react-table"
import { match } from "ts-pattern"
import dayjs from "dayjs"
import { WorkingTimeImportStatusBadge } from "../../-components/working-time-bookings/WorkingTimeImportStatusBadge"
import { WorkingTimeBookingRequestDetailsModal } from "./-components/WorkingTImeBookingRequestDetailsModal"
import { DepartmentWorkAreaTree } from "./-components/DepartmentWorkAreaTree"
import { useBreakpoints } from "@/utils/use-breakpoints"
import { WorkAreaName } from "@components/WorkAreaName"
import { useTranslation } from "react-i18next"
import { useDefaultInfiniteQuery } from "@/utils/useDefaultInfiniteQuery"
import { useUserLocalStorage } from "../../-components/use-user-local-storage"
import { Dispatch, SetStateAction } from "react"
import { DeferredMultiSelect } from "../../../../-components/DeferredMultiSelect"

export interface SearchParams {
  dateRange?: DatesRangeValue<string>
  personId?: number
  status?: WorkingTimeImportStatus
  workAreaIds?: string[]
  statuses?: WorkingTimeImportStatus[] | string | null
  sorting?: SortingState
}

const DEFAULT_SORTING = [{ id: "timeStamp", desc: true }]
const ALL_WORKING_TIME_STATUSES = Object.values(WorkingTimeImportStatus)
const STATUS_ORDER = new Map(
  ALL_WORKING_TIME_STATUSES.map((status, index) => [status, index])
)

function parseStatusInput(value: SearchParams["statuses"]) {
  if (Array.isArray(value)) return value
  if (typeof value !== "string") return []

  const trimmed = value.trim()
  if (trimmed.length === 0) return []

  if (trimmed.startsWith("[") && trimmed.endsWith("]")) {
    try {
      const parsed = JSON.parse(trimmed)
      return Array.isArray(parsed) ? parsed : []
    } catch {
      return []
    }
  }

  return trimmed.split(",")
}

function toStatuses(value: SearchParams["statuses"]) {
  const parsed = parseStatusInput(value)
  const normalized = parsed.filter(
    (status): status is WorkingTimeImportStatus =>
      typeof status === "string" &&
      ALL_WORKING_TIME_STATUSES.includes(status as WorkingTimeImportStatus)
  )

  return Array.from(new Set(normalized)).sort(
    (left, right) =>
      (STATUS_ORDER.get(left) ?? Number.MAX_SAFE_INTEGER) -
      (STATUS_ORDER.get(right) ?? Number.MAX_SAFE_INTEGER)
  )
}

function areEqualStatuses(
  left: WorkingTimeImportStatus[],
  right: WorkingTimeImportStatus[]
) {
  if (left.length !== right.length) return false
  return left.every((status, index) => status === right[index])
}

export const Route = createFileRoute("/app/duties/working-time-booking/")({
  component: RouteComponent,
  validateSearch: (search: SearchParams) => ({
    ...search,
    sorting: search.sorting || DEFAULT_SORTING,
  }),
})

function RouteComponent() {
  const { t, i18n } = useTranslation("app_server")
  const today = dayjs()

  const [filterVisible, setFilterVisible] = useUserLocalStorage({
    key: "working-time-booking_filter-visible",
    defaultValue: false,
  })
  const queryOptions = useResponsiveFilterQueryOptions(filterVisible)
  const search = Route.useSearch()
  const [dateRange, setDateRange] = React.useState<DatesRangeValue<string>>(
    search.dateRange ?? [null, null]
  )
  const navigate = Route.useNavigate()
  const selectedStatuses = React.useMemo(
    () => toStatuses(search.statuses),
    [search.statuses]
  )
  const statusOptions = React.useMemo(
    () =>
      ALL_WORKING_TIME_STATUSES.map((status) => ({
        value: status,
        label: i18n.t(`WorkingTimeImportStatus_${status}`, {
          ns: "app_server",
          defaultValue: status,
          returnObjects: false,
        }),
      })),
    [i18n.language]
  )
  const [selectedRequest, setSelectedRequest] = React.useState<
    WorkingTimeBookingDto | undefined
  >()

  React.useEffect(() => {
    if (dateRange[0] && dateRange[1]) {
      navigate({
        search: {
          ...search,
          dateRange: dateRange,
        },
        replace: true,
      })
    }
    if (!dateRange[0] && !dateRange[1] && search.dateRange) {
      navigate({
        search: {
          ...search,
          dateRange: undefined,
        },
        replace: true,
      })
    }
  }, [dateRange])

  const getRowProps = React.useCallback(function (
    row: Row<WorkingTimeBookingDto>
  ): RowProps | undefined {
    if (!row.original.canApprove) return undefined

    const status = row.original.status

    return match<WorkingTimeImportStatus, RowProps | undefined>(status)
      .with("Requested", "Approved", "Rejected", () => {
        return {
          onClick: () => setSelectedRequest(row.original),
          bd: "solid 1px",
        }
      })
      .otherwise(() => undefined)
  }, [])

  const columnDefinitions = React.useMemo<ColumnDef<WorkingTimeBookingDto>[]>(
    () => [
      ...(search.personId
        ? []
        : [
            {
              accessorKey: "personId",
              cell: ({ row }) => (
                <PersonName personId={row.original.personId} />
              ),
              header: t("Person"),
            } as ColumnDef<WorkingTimeBookingDto>,
          ]),
      {
        accessorKey: "workAreaId",
        header: t("Department"),
        cell: ({ row }) =>
          !!row.original.workAreaId && (
            <WorkAreaName workAreaId={row.original.workAreaId} />
          ),
      },
      {
        accessorKey: "timeStamp",
        header: t("Time"),
        cell: ({ row }) =>
          getFormattedTimestamp(row.original.timeStamp, search.dateRange),
      },
      { accessorKey: "type.name", header: t("Type") },
      { accessorKey: "source", header: t("Source") },
      { accessorKey: "note", header: t("Note") },
      {
        accessorKey: "status",
        header: t("Status"),
        cell: ({ row }) => (
          <WorkingTimeImportStatusBadge status={row.original.status} />
        ),
      },
    ],
    [search.personId, search.dateRange]
  )
  const { xs } = useBreakpoints()
  const filterWidth = xs ? "100%" : 250
  const sortingState: [SortingState, Dispatch<SetStateAction<SortingState>>] = [
    search.sorting,
    (sorting) =>
      typeof sorting === "function"
        ? navigate({
            search: { ...search, sorting: sorting(search.sorting) },
            replace: true,
          })
        : navigate({ search: { ...search, sorting }, replace: true }),
  ]

  const buildQueryParams = React.useCallback(
    (pageRequest: { pageParam: number; pageSize: number }) => ({
      ...search,
      statuses: selectedStatuses.length > 0 ? selectedStatuses : undefined,
      from: search.dateRange?.[0] ?? undefined,
      to: search.dateRange?.[1] ?? undefined,
      ...pageRequest,
    }),
    [search, selectedStatuses]
  )
  return (
    <Page
      buttonGroupProps={{
        children: (
          <ResponsiveFilterGroup.Button
            filters={{
              person: search.personId,
              dateRange: search.dateRange,
              workAreas: search.workAreaIds,
              statuses: search.statuses,
            }}
            visible={filterVisible}
            onChange={() => setFilterVisible(!filterVisible)}
          />
        ),
      }}
    >
      <Stack h={"100%"} gap={"xs"}>
        <ResponsiveFilterGroup
          w={"100%"}
          visible={filterVisible}
          setVisible={setFilterVisible}
          filters={{
            person: search.personId,
            dateRange: search.dateRange,
            workAreas: search.workAreaIds,
            statuses: search.statuses,
          }}
          onReset={() => {
            setDateRange([null, null])
            navigate({
              search: {
                ...search,
                personId: undefined,
                dateRange: undefined,
                workAreaIds: undefined,
                statuses: undefined,
              },
              replace: true,
            })
          }}
        >
          <InfinitePersonSingleSelect
            value={search.personId?.toString()}
            onSelectionChange={(v) =>
              navigate({
                search: {
                  ...search,
                  personId: v === null ? undefined : parseInt(v),
                },
                replace: true,
              })
            }
            inputProps={{ w: filterWidth }}
          />
          <DatePickerInput
            label={t("TimePeriod")}
            type="range"
            value={dateRange}
            valueFormat="L"
            allowSingleDateInRange
            clearable
            onChange={setDateRange}
            w={filterWidth}
            presets={[
              {
                value: [today.format("YYYY-MM-DD"), today.format("YYYY-MM-DD")],
                label: t("Today"),
              },
              {
                value: [
                  today.subtract(1, "day").format("YYYY-MM-DD"),
                  today.subtract(1, "day").format("YYYY-MM-DD"),
                ],
                label: t("Yesterday"),
              },
              {
                value: [
                  today.subtract(2, "day").format("YYYY-MM-DD"),
                  today.format("YYYY-MM-DD"),
                ],
                label: t("LastTwoDays"),
              },
              {
                value: [
                  today.subtract(7, "day").format("YYYY-MM-DD"),
                  today.format("YYYY-MM-DD"),
                ],
                label: t("Last7Days"),
              },
            ]}
          />
          <DepartmentWorkAreaTree
            maxPills={1}
            initialCheckedState={search.workAreaIds}
            onCheckedStateChange={(v) =>
              navigate({
                search: {
                  ...search,
                  workAreaIds: v && v.length > 0 ? v : undefined,
                },
                replace: true,
              })
            }
            w={filterWidth}
          />
          <DeferredMultiSelect
            clearable
            searchable
            label={t("Status")}
            w={filterWidth}
            data={statusOptions}
            value={selectedStatuses}
            normalize={(next) => toStatuses(next as WorkingTimeImportStatus[])}
            isEqual={(left, right) =>
              areEqualStatuses(
                left as WorkingTimeImportStatus[],
                right as WorkingTimeImportStatus[]
              )
            }
            onCommit={(next) => {
              const normalized = toStatuses(next as WorkingTimeImportStatus[])
              if (areEqualStatuses(selectedStatuses, normalized)) return

              navigate({
                search: {
                  ...search,
                  statuses: normalized.length > 0 ? normalized : undefined,
                },
                replace: true,
              })
            }}
            comboboxProps={{ withinPortal: true }}
            maxDropdownHeight={280}
          />
        </ResponsiveFilterGroup>
        <InfiniteScrollingTable
          id="working-time-bookings-table"
          infiniteQueryResult={useDefaultInfiniteQuery(
            getPagedWorkingTimeBookings,
            buildQueryParams,
            queryOptions
          )}
          columnDefinitions={columnDefinitions}
          getRowProps={getRowProps}
          sortingState={sortingState}
        />
      </Stack>
      <WorkingTimeBookingRequestDetailsModal
        request={selectedRequest!}
        opened={!!selectedRequest}
        onClose={() => setSelectedRequest(undefined)}
      />
    </Page>
  )
}

function getFormattedTimestamp(
  timestamp: string,
  dateRange?: DatesRangeValue<string>
) {
  if (dateRange && dateRange[0] && dateRange[1]) {
    const start = dayjs(dateRange[0])
    const end = dayjs(dateRange[1])
    if (start.isSame(end, "date")) {
      if (dayjs(timestamp).isSame(start, "date")) {
        return dayjs(timestamp).format("LT")
      } else {
        return dayjs(timestamp).format("LT +1")
      }
    }
  }
  return getFormattedDate(timestamp, "L LT")
}
